import React, { useContext, useEffect, useState } from "react";
import Chart from "react-apexcharts";
import AuthContext from "../AuthContext";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

function Dashboard() {
  const [saleAmount, setSaleAmount] = useState(0);
  const [purchaseAmount, setPurchaseAmount] = useState(0);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [monthlySales, setMonthlySales] = useState([]);
  const [productStockData, setProductStockData] = useState({ labels: [], datasets: [] });


  // Bar Chart Configuration (Monthly Sales)
  const [barChart, setBarChart] = useState({
    options: {
      chart: { id: "monthly-sales-bar" },
      xaxis: { categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] },
      title: { text: "Monthly Sales Amount (Rs.)", align: "center" },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        },
        style: {
          fontSize: "12px",
          colors: ["#000"],
        },
        offsetY: -20, // Position above bar
      },
      yaxis: {
        labels: {
          formatter: function (val) {
            return "Rs." + val.toLocaleString("en-IN");
          },
        },
      },
      plotOptions: {
        bar: {
          dataLabels: {
            position: "top", // Ensure labels are above bars
          },
        },
      },
    },
    series: [{ name: "Sales Amount (Rs.)", data: [] }],
  });

  // Fetch data every 30 seconds
  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = () => {
    fetchTotalSaleAmount();
    fetchTotalPurchaseAmount();
    fetchStoresData();
    fetchProductsData();
    fetchMonthlySalesData();
  };

  const fetchTotalSaleAmount = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_total_sales.php`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        console.log("Raw Sales Response:", text);
        const data = JSON.parse(text);
        setSaleAmount(parseFloat(data.totalSaleAmount) || 0);
      })
      .catch((err) => {
        console.log("Fetch total sales error:", err);
        setSaleAmount(0);
      });
  };

  const fetchTotalPurchaseAmount = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_total_purchases.php`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        console.log("Raw Purchases Response:", text);
        const data = JSON.parse(text);
        setPurchaseAmount(parseFloat(data.totalPurchaseAmount) || 0);
      })
      .catch((err) => {
        console.log("Fetch total purchases error:", err);
        setPurchaseAmount(0);
      });
  };

  const fetchStoresData = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_stores.php`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setStores(Array.isArray(data.data) ? data.data : data);
      })
      .catch((err) => {
        console.log("Fetch stores error:", err);
        setStores([]);
      });
  };

  const fetchProductsData = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_products.php`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const productsArray = Array.isArray(data) ? data : [];
        setProducts(productsArray);
        updateProductStockChart(productsArray);
      })
      .catch((err) => {
        console.log("Fetch products error:", err);
        setProducts([]);
      });
  };

  const fetchMonthlySalesData = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_monthly_sales.php`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        console.log("Raw Monthly Sales Response:", text);
        const data = JSON.parse(text);
        const monthlyData = Array(12).fill(0);
        data.forEach((item) => {
          const monthIndex = new Date(item.month).getMonth();
          monthlyData[monthIndex] = parseFloat(item.total_sales) || 0;
        });
        setMonthlySales(monthlyData);
        setBarChart((prev) => ({
          ...prev,
          series: [{ name: "Sales Amount (Rs.)", data: monthlyData }],
        }));
      })
      .catch((err) => {
        console.log("Fetch monthly sales error:", err);
        setMonthlySales(Array(12).fill(0));
      });
  };

  // Dynamic color generator for 50+ products
  const generateColors = (count) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
      const hue = (i * 360) / count; // Spread hues evenly
      colors.push(`hsla(${hue}, 70%, 50%, 0.2)`); // Background
    }
    return colors;
  };

  const updateProductStockChart = (productsData) => {
    const topN = 10; // Show top 10 products
    const sortedProducts = [...productsData].sort((a, b) => (b.in_stock || 0) - (a.in_stock || 0));
    const topProducts = sortedProducts.slice(0, topN);
    const othersStock = sortedProducts.slice(topN).reduce((sum, p) => sum + (p.in_stock || 0), 0);

    const labels = [...topProducts.map((p) => p.product_name), ...(othersStock > 0 ? ["Others"] : [])];
    const stockData = [...topProducts.map((p) => p.in_stock || 0), ...(othersStock > 0 ? [othersStock] : [])];
    const colorCount = labels.length;
    const backgroundColors = generateColors(colorCount);
    const borderColors = backgroundColors.map((color) => color.replace("0.2", "1"));

    setProductStockData({
      labels,
      datasets: [
        {
          label: "Stock in Inventory",
          data: stockData,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
        },
      ],
    });
  };

  return (
    <div className="grid grid-cols-1 col-span-12 lg:col-span-10 gap-6 md:grid-cols-3 lg:grid-cols-4 p-4">
      <article className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-white p-6">
        <div className="inline-flex gap-2 self-end rounded bg-green-100 p-1 text-green-600">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          <span className="text-xs font-medium">Live</span>
        </div>
        <div>
          <strong className="block text-sm font-medium text-gray-500">Total Sales</strong>
          <p>
            <span className="text-2xl font-medium text-gray-900">
              Rs.{typeof saleAmount === "number" ? saleAmount.toFixed(2) : "0.00"}
            </span>
          </p>
        </div>
      </article>

      <article className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-white p-6">
        <div className="inline-flex gap-2 self-end rounded bg-red-100 p-1 text-red-600">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
          </svg>
          <span className="text-xs font-medium">Live</span>
        </div>
        <div>
          <strong className="block text-sm font-medium text-gray-500">Total Purchases</strong>
          <p>
            <span className="text-2xl font-medium text-gray-900">
              Rs.{typeof purchaseAmount === "number" ? purchaseAmount.toFixed(2) : "0.00"}
            </span>
          </p>
        </div>
      </article>

      <article className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-white p-6">
        <div className="inline-flex gap-2 self-end rounded bg-blue-100 p-1 text-blue-600">
          <span className="text-xs font-medium">Live</span>
        </div>
        <div>
          <strong className="block text-sm font-medium text-gray-500">Total Products</strong>
          <p>
            <span className="text-2xl font-medium text-gray-900">{products.length}</span>
          </p>
        </div>
      </article>

      <article className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-white p-6">
        <div className="inline-flex gap-2 self-end rounded bg-blue-100 p-1 text-blue-600">
          <span className="text-xs font-medium">Live</span>
        </div>
        <div>
          <strong className="block text-sm font-medium text-gray-500">Total Stores</strong>
          <p>
            <span className="text-2xl font-medium text-gray-900">{stores.length}</span>
          </p>
        </div>
      </article>

      <div className="flex flex-col lg:flex-row justify-around bg-white rounded-lg py-8 col-span-full gap-6">
        {monthlySales.length > 0 && (
          <div>
            <Chart options={barChart.options} series={barChart.series} type="bar" width="500" />
          </div>
        )}
        {productStockData.labels.length > 0 && (
          <div>
            <Doughnut
              data={productStockData}
              options={{
                plugins: {
                  title: { display: true, text: "Top 10 Product Stock Distribution (Others Combined)" },
                  legend: { position: "right", maxWidth: 200, labels: { boxWidth: 20 } },
                },
                maintainAspectRatio: false,
              }}
              height={300}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;